<template>
  <div
    class="w-full wrapper"
    v-loading.fullscreen.lock="loading"
    :element-loading-text="$t('connecting')"
    element-loading-spinner="el-icon-loading"
    element-loading-background="rgba(0, 0, 0, 0.8)"
  >
    <div class="page__card">
      <div class="page__card-title">
        <h1 class="sm:whitespace-nowrap">{{ $t("statistics") }}</h1>
        <div class="page__card-item">
          <router-link
            v-if="role != 'reservoir'"
            to="/2"
            class="page__card-router"
            :class="{ 'page__card-router--active': !$route.query.page }"
          >
            <IconBuilding4 class="router__icon" />
            <p class="router__text">{{ $t("building_and_structures") }}</p>
          </router-link>
          <router-link
            v-if="role == 'admin' || role == 'supervisor'"
            to="/2?page=2"
            class="page__card-router"
            :class="{ 'page__card-router--active': $route.query.page == 2 }"
          >
            <IconCompany class="router__icon" />
            <p class="router__text">{{ $t("organizations") }}</p>
          </router-link>
          <router-link
            v-if="['admin', 'org_admin', 'ministry_admin', 'main_org_admin', 'supervisor'].includes(role)"
            to="/2?page=3"
            class="page__card-router"
            :class="{ 'page__card-router--active': $route.query.page == 3 }"
          >
            <HMapLocation04 class="router__icon" />
            <p class="router__text">{{ $t("in_terms_regions") }}</p>
          </router-link>
          <router-link
            v-if="['admin', 'org_admin', 'ministry_admin', 'main_org_admin', 'supervisor'].includes(role)"
            to="/2?page=4"
            class="page__card-router"
            :class="{ 'page__card-router--active': $route.query.page == 4 }"
          >
            <IconBridge1 class="router__icon" />
            <p class="router__text">{{ $t("transport_and_facilities") }}</p>
          </router-link>
          <router-link
            v-if="['admin', 'reservoir', 'supervisor'].includes(role)"
            to="/2?page=5"
            class="page__card-router"
            :class="{ 'page__card-router--active': $route.query.page == 5 }"
          >
            <IconHydroBuilding class="router__icon" />
            <p class="router__text">{{ $t("hydraulic_structures") }}</p>
          </router-link>
        </div>
      </div>
      <div class="mt-5 gap-2 flex flex-wrap items-center">
        <div v-if="!$route.query.page" class="w-52">
          <el-select
            v-model="region"
            :placeholder="$t('all_provinces')"
            @change="changeRegion"
          >
            <el-option :value="undefined" :label="$t('all_provinces')" />
            <el-option
              v-for="item in regions"
              :key="item.id"
              :value="item.id"
              :label="
                item[`name_${$i18n.locale}`]
                  ? item[`name_${$i18n.locale}`]
                  : item.name_cyr
              "
            ></el-option>
          </el-select>
        </div>

        <button
          v-if="!$route.query.page"
          type="button"
          :disabled="!region"
          class="interface-btn bg-rose-50 text-rose-600 hover:bg-rose-100"
          @click="clearFilter"
        >
          <IconBroom class="icon" />
        </button>
      </div>
      <div class="mt-5">
        <BuildingStatistics
          v-if="!$route.query.page"
          :data="buildingStatistics"
          :loading="loadingBuildingStatistics"
        />

        <OrganizationStatistics
          v-if="$route.query.page == 2"
          :data="organizationStatistics"
          :loading="loadingOrganizationStatistics"
        />
        <RegionStatistics
          v-if="$route.query.page == 3"
          :data="regionStatistics"
          :loading="loadingRegionStatistics"
        />
        <TransportStatistics
          v-if="$route.query.page == 4"
          :data="transportStatistics"
          :loading="loadingTransportStatistics"
        />
        <HydroStatistics
          v-if="$route.query.page == 5"
          :data="buildingStatistics"
          :loading="loadingBuildingStatistics"
        />
      </div>
    </div>
  </div>
</template>

<script>
import BuildingStatistics from "@/components/Statistic/Building.vue";
import RegionStatistics from "@/components/Statistic/Region.vue";
import HydroStatistics from "@/components/Statistic/Hydro.vue";
import TransportStatistics from "@/components/Statistic/Transport.vue";
import IconBuilding4 from "@/components/Icons/iconBuilding4.vue";
import IconCompany from "@/components/Icons/iconCompany.vue";
import HMapLocation04 from "@/components/Icons/hMapLocation04.vue";
import IconBridge1 from "@/components/Icons/iconBridge1.vue";
import IconHydroBuilding from "@/components/Icons/iconHydroBuilding.vue";
import IconBroom from "@/components/Icons/iconBroom.vue";
import OrganizationStatistics from "@/components/Statistic/Organization.vue";

export default {
  name: "StatisticPage",
  components: {
    BuildingStatistics,
    RegionStatistics,
    OrganizationStatistics,
    HydroStatistics,
    TransportStatistics,
    IconBuilding4,
    IconCompany,
    HMapLocation04,
    IconBridge1,
    IconHydroBuilding,
    IconBroom,
  },
  data() {
    return {
      loading: false,
      region: +this.$route.query.region || undefined,
      loadingBuildingStatistics: false,
      buildingStatistics: {},
      loadingTransportStatistics: false,
      transportStatistics: {},
      loadingOrganizationStatistics: false,
      organizationStatistics: [],
      loadingRegionStatistics: false,
      regionStatistics: [],
    };
  },
  computed: {
    role() {
      return this.$store.state.role;
    },
    regions() {
      return this.$store.state.dictionary.region;
    },
  },
  methods: {
    changeRegion(e) {
      this.getBuildingStatistics();
      this.$router.push({
        query: { ...this.$route.query, region: e },
      });
    },

    getBuildingStatistics() {
      this.loadingBuildingStatistics = true;
      axios
        .get(`/building/statistic/${this.region ? this.region + "/" : ""}`)
        .then((res) => {
          this.buildingStatistics = res.data;
        })
        .finally(() => {
          this.loadingBuildingStatistics = false;
        });
    },
    getTransportStatistics() {
      this.loadingTransportStatistics = true;
      axios
        .get(`/transport_facility_statistic/`)
        .then((res) => {
          this.transportStatistics = res.data;
        })
        .finally(() => {
          this.loadingTransportStatistics = false;
        });
    },
    getOrganizationStatistics(id) {
      this.loadingOrganizationStatistics = true;
      axios
        .get(`/building/statisticorganization/${id || 1514}/`)
        .then((res) => {
          this.organizationStatistics = res.data;
        })
        .finally(() => {
          this.loadingOrganizationStatistics = false;
        });
    },
    getRegionStatistics() {
      this.loadingRegionStatistics = true;
      axios
        .get(`/building/organization/statistic/`)
        .then((res) => {
          this.regionStatistics = res.data;
        })
        .finally(() => {
          this.loadingRegionStatistics = false;
        });
    },

    clearFilter() {
      this.region = undefined;
      this.changeRegion(undefined);
    },
  },
  mounted() {
    this.getBuildingStatistics();
    this.getTransportStatistics();
    this.getOrganizationStatistics();
  },
};
</script>

<style lang="scss" scoped>
.page__card-item {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}

.page__card-router {
  padding: 8px 16px;
  border-radius: 12px;
  transition: all 0.3s ease;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  .router__text {
    font-weight: 600;
    line-height: 22px;
  }

  .router__icon {
    color: #0891b2;
    width: 24px;
    height: 24px;
  }

  &:hover {
    background: #f1f5f9;
  }

  &--active {
    background: #3730a3;
    color: white;

    &:hover {
      background: #4338ca;
    }

    .router__icon {
      color: white;
    }
  }
}
</style>
